/**
 * /payroll-l3/contracts — Contract upload & review.
 *
 * The "Review draft rows" target page (/payroll-l3/contracts/{id}/review) is
 * an L3.3 deliverable; this page links to it but flags it as not-yet-built.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { api, getToken } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { FileDropZone } from "@/components/FileDropZone";

export const Route = createFileRoute("/_auth/payroll-l3/contracts")({
  component: ContractsPage,
});

interface UnionContract {
  id: string;
  cba_id: string | null;
  local_id: string | null;
  file_name: string;
  file_mime: string;
  file_size_bytes: number;
  uploaded_by_user_id: string;
  uploaded_at: string;
  effective_date: string | null;
  parse_status: string;
  parsed_classification_count: number | null;
  parsed_wage_rate_count: number | null;
  parsed_fringe_count: number | null;
  parse_error: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parsed_payload: Record<string, any> | null;
}

interface CBALite {
  id: string;
  trade_name: string;
  local_id: string;
}

interface LocalLite {
  id: string;
  local_number: string;
  name: string;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusChip(s: string) {
  const map: Record<string, "default" | "info" | "warning" | "success" | "error"> = {
    uploaded: "default",
    parsing: "warning",
    parsed: "info",
    confirmed: "success",
    error: "error",
    failed: "error",
  };
  return <Chip label={s} size="small" color={map[s] ?? "default"} />;
}

function ContractsPage() {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detail, setDetail] = useState<UnionContract | null>(null);

  const contractsQ = useQuery({
    queryKey: ["payroll-l3", "contracts"],
    queryFn: () => api<UnionContract[]>("/api/payroll-l3/contracts"),
  });
  const cbasQ = useQuery({
    queryKey: ["payroll-l3", "cbas"],
    queryFn: () => api<CBALite[]>("/api/payroll-l3/cbas?include_inactive=true"),
  });
  const localsQ = useQuery({
    queryKey: ["payroll-l3", "locals"],
    queryFn: () => api<LocalLite[]>("/api/payroll-l3/locals?include_inactive=true"),
  });

  const cbaById = (id: string | null) =>
    id ? cbasQ.data?.find((c) => c.id === id)?.trade_name ?? id : "—";
  const localById = (id: string | null) =>
    id ? localsQ.data?.find((l) => l.id === id) : null;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>Union Contracts</Typography>
        <Stack direction="row" alignItems="center" gap={2}>
          <HelpHint docPath="payroll-l3/cba-setup.md#uploading-and-storing-the-contract" label="Contract upload help" />
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Upload Contract
          </Button>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Archive of signed CBA documents. Once parsed, draft classifications
        and rates can be reviewed and promoted into the CBA.
      </Typography>

      {contractsQ.isLoading && <CircularProgress size={24} />}
      {contractsQ.error && <Alert severity="error">Failed to load contracts.</Alert>}

      {contractsQ.data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>File</TableCell>
              <TableCell>Local</TableCell>
              <TableCell>CBA</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {contractsQ.data.map((c) => {
              const lc = localById(c.local_id);
              return (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Stack>
                      <span style={{ fontWeight: 600 }}>{c.file_name}</span>
                      <Typography variant="caption" color="text.secondary">
                        {fmtBytes(c.file_size_bytes)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{lc ? `Local ${lc.local_number} — ${lc.name}` : "—"}</TableCell>
                  <TableCell>{cbaById(c.cba_id)}</TableCell>
                  <TableCell>{fmtDate(c.uploaded_at)}</TableCell>
                  <TableCell>{statusChip(c.parse_status)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setDetail(c)}>View</Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {contractsQ.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  No contracts uploaded yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        cbas={cbasQ.data ?? []}
        locals={localsQ.data ?? []}
        onUploaded={() => {
          qc.invalidateQueries({ queryKey: ["payroll-l3", "contracts"] });
          setUploadOpen(false);
        }}
      />

      <ContractDetailDialog
        contractId={detail?.id ?? null}
        onClose={() => setDetail(null)}
        onChanged={() => qc.invalidateQueries({ queryKey: ["payroll-l3", "contracts"] })}
        cbaName={cbaById}
        localById={localById}
      />
    </Box>
  );
}

function UploadDialog({
  open,
  onClose,
  cbas,
  locals,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  cbas: CBALite[];
  locals: LocalLite[];
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [cbaId, setCbaId] = useState("");
  const [localId, setLocalId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Choose a file first");
      const fd = new FormData();
      fd.append("file", file);
      if (cbaId) fd.append("cba_id", cbaId);
      if (localId) fd.append("local_id", localId);
      if (effectiveDate) fd.append("effective_date", effectiveDate);

      // ofetch handles FormData natively, but we drop through to fetch here
      // to avoid Content-Type mangling.
      const token = getToken();
      const res = await fetch("/api/payroll-l3/contracts/upload", {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Upload failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      setFile(null);
      setCbaId("");
      setLocalId("");
      setEffectiveDate("");
      setError(null);
      onUploaded();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Upload failed"),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload CBA Contract</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FileDropZone
            file={file}
            onFile={setFile}
            onClear={() => setFile(null)}
            accept=".pdf,.doc,.docx,.txt"
            label="Drop or click to select contract"
            sublabel="PDF · DOC · DOCX · TXT"
          />
          <TextField
            select
            label="CBA (optional)"
            value={cbaId}
            onChange={(e) => setCbaId(e.target.value)}
            helperText="Link to an existing CBA to populate it via parse review"
          >
            <MenuItem value="">—</MenuItem>
            {cbas.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.trade_name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Local (optional)"
            value={localId}
            onChange={(e) => setLocalId(e.target.value)}
          >
            <MenuItem value="">—</MenuItem>
            {locals.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                Local {l.local_number} — {l.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Effective Date (optional)"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!file || uploadMut.isPending}
          onClick={() => uploadMut.mutate()}
        >
          {uploadMut.isPending ? "Uploading…" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ContractDetailDialog({
  contractId,
  onClose,
  onChanged,
  cbaName,
  localById,
}: {
  contractId: string | null;
  onClose: () => void;
  onChanged: () => void;
  cbaName: (id: string | null) => string;
  localById: (id: string | null) => LocalLite | null | undefined;
}) {
  const open = !!contractId;
  const navigate = useNavigate();
  const [parseError, setParseError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);

  // Live contract — refetches after parse/confirm
  const contractQ = useQuery({
    queryKey: ["payroll-l3", "contract", contractId],
    queryFn: () => api<UnionContract>(`/api/payroll-l3/contracts/${contractId}`),
    enabled: !!contractId,
    refetchInterval: (query) =>
      query.state.data?.parse_status === "parsing" ? 2000 : false,
  });
  const contract = contractQ.data ?? null;

  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMut = useMutation({
    mutationFn: () => api(`/api/payroll-l3/contracts/${contractId}`, { method: "DELETE" }),
    onSuccess: () => { onChanged(); onClose(); },
    onError: (e: unknown) => setConfirmDelete(false),
  });

  const parseMut = useMutation({
    mutationFn: () => api(`/api/payroll-l3/contracts/${contractId}/parse`, { method: "POST" }),
    onSuccess: () => {
      setParseError(null);
      contractQ.refetch();
      onChanged();
    },
    onError: (e: unknown) => setParseError(e instanceof Error ? e.message : "Parse failed"),
  });

  const confirmMut = useMutation({
    mutationFn: () => api(`/api/payroll-l3/contracts/${contractId}/confirm`, { method: "POST" }),
    onSuccess: () => {
      setConfirmError(null);
      contractQ.refetch();
      onChanged();
    },
    onError: (e: unknown) => setConfirmError(e instanceof Error ? e.message : "Confirm failed"),
  });

  const payload = contract?.parsed_payload;
  const union = payload?.union;
  const local = payload?.local;
  const cba = payload?.cba;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <span>{contract?.file_name ?? "Contract"}</span>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {contractQ.isLoading && <CircularProgress size={24} />}
        {contract && (
          <Stack spacing={2}>
            {/* Status */}
            <Box>
              <Typography variant="caption" color="text.secondary">Parse Status</Typography>
              <Box mt={0.5}>{statusChip(contract.parse_status)}</Box>
              {contract.parse_error && (
                <Alert severity="error" sx={{ mt: 1 }}>{contract.parse_error}</Alert>
              )}
            </Box>

            {/* File info */}
            <Box>
              <Typography variant="caption" color="text.secondary">File</Typography>
              <Typography>{fmtBytes(contract.file_size_bytes)} · {contract.file_mime}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Uploaded</Typography>
              <Typography>{fmtDate(contract.uploaded_at)}</Typography>
            </Box>

            {/* Parse results summary */}
            {contract.parse_status === "parsing" && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                AI is reading the PDF — this takes 15–30 seconds…
              </Alert>
            )}

            {(contract.parse_status === "parsed" || contract.parse_status === "confirmed") && payload && (
              <Box sx={{ bgcolor: "grey.50", borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Extracted from PDF</Typography>
                <Stack spacing={0.5}>
                  {union?.name && (
                    <Typography variant="body2">
                      <strong>Union:</strong> {union.name} {union.abbreviation ? `(${union.abbreviation})` : ""}
                    </Typography>
                  )}
                  {local?.local_number && (
                    <Typography variant="body2">
                      <strong>Local:</strong> {local.local_number}{local.name ? ` — ${local.name}` : ""}{local.city ? `, ${local.city}` : ""}{local.state_code ? `, ${local.state_code}` : ""}
                    </Typography>
                  )}
                  {cba?.trade_name && (
                    <Typography variant="body2">
                      <strong>Trade:</strong> {cba.trade_name}
                    </Typography>
                  )}
                  {(cba?.effective_start || cba?.effective_end) && (
                    <Typography variant="body2">
                      <strong>Period:</strong> {cba.effective_start ?? "?"} → {cba.effective_end ?? "ongoing"}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>{contract.parsed_classification_count ?? 0}</strong> classifications &nbsp;·&nbsp;
                    <strong>{contract.parsed_wage_rate_count ?? 0}</strong> wage rates &nbsp;·&nbsp;
                    <strong>{contract.parsed_fringe_count ?? 0}</strong> fringes
                  </Typography>
                  {payload.overall_confidence != null && (
                    <Typography variant="caption" color="text.secondary">
                      Overall confidence: {Math.round(payload.overall_confidence * 100)}%
                    </Typography>
                  )}
                  {(payload.parse_notes ?? []).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        color="warning"
                        startIcon={notesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        onClick={() => setNotesOpen((o) => !o)}
                        sx={{ px: 0, minWidth: 0, textTransform: "none", fontSize: "0.75rem" }}
                      >
                        {notesOpen ? "Hide" : "Show"} {(payload.parse_notes as string[]).length} parse note{(payload.parse_notes as string[]).length !== 1 ? "s" : ""}
                      </Button>
                      {notesOpen && (
                        <Alert severity="warning" sx={{ mt: 0.5 }}>
                          {(payload.parse_notes as string[]).map((n, i) => (
                            <div key={i}>{n}</div>
                          ))}
                        </Alert>
                      )}
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            {/* Confirm result */}
            {contract.parse_status === "confirmed" && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                action={
                  contract.cba_id ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        navigate({ to: "/payroll-l3/cbas", search: { cba: contract.cba_id! } })
                      }
                    >
                      View rate sheet →
                    </Button>
                  ) : undefined
                }
              >
                Union, local, CBA, and all classifications are now active in your payroll setup.
              </Alert>
            )}

            {/* Errors */}
            {parseError && <Alert severity="error">{parseError}</Alert>}
            {confirmError && <Alert severity="error">{confirmError}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {!confirmDelete ? (
          <IconButton
            size="small"
            color="error"
            title="Delete this contract"
            onClick={() => setConfirmDelete(true)}
            disabled={deleteMut.isPending}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2" color="error">Delete?</Typography>
            <Button size="small" color="error" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? "Deleting…" : "Yes"}
            </Button>
            <Button size="small" onClick={() => setConfirmDelete(false)}>No</Button>
          </Stack>
        )}
        <Box flex={1} />
        {contract?.parse_status === "uploaded" || contract?.parse_status === "parse_failed" ? (
          <Button
            variant="contained"
            startIcon={parseMut.isPending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={parseMut.isPending}
            onClick={() => parseMut.mutate()}
          >
            {parseMut.isPending ? "Parsing…" : "Parse with AI"}
          </Button>
        ) : contract?.parse_status === "parsed" ? (
          <Button
            variant="contained"
            color="success"
            startIcon={confirmMut.isPending ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            disabled={confirmMut.isPending}
            onClick={() => confirmMut.mutate()}
          >
            {confirmMut.isPending ? "Confirming…" : "Confirm & Activate"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
